import { useState, useEffect } from 'react';
import { employeesAPI, companiesAPI, authAPI } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Trash2, Edit2, UserPlus, Save, X, KeyRound } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

export default function EmployeeManageDialog({ open, onClose, onSave }) {
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [accountForm, setAccountForm] = useState({ open: false, employeeId: null, email: '', password: '' });
  const [form, setForm] = useState({ first_name: '', last_name: '', initials: '', contract_hours: '35', company_id: 'none' });

  useEffect(() => {
    if (open) { fetchEmployees(); fetchCompanies(); }
  }, [open]);

  const fetchEmployees = async () => {
    try { const res = await employeesAPI.list(); setEmployees(res.data); } catch { /* */ }
  };
  const fetchCompanies = async () => {
    try { const res = await companiesAPI.list(); setCompanies(res.data); } catch { /* */ }
  };

  const resetForm = () => {
    setForm({ first_name: '', last_name: '', initials: '', contract_hours: '35', company_id: 'none' });
    setShowAdd(false);
    setEditId(null);
  };

  const getCompanyName = (id) => companies.find(c => c.id === id)?.name || '—';

  const handleAdd = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.initials.trim()) {
      toast.error('Remplissez tous les champs obligatoires'); return;
    }
    setLoading(true);
    try {
      await employeesAPI.create({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        initials: form.initials.trim(),
        contract_hours: parseFloat(form.contract_hours),
        company_id: form.company_id && form.company_id !== 'none' ? form.company_id : null,
      });
      toast.success('Employé ajouté');
      resetForm(); fetchEmployees(); onSave();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setLoading(false);
  };

  const handleEdit = (emp) => {
    setEditId(emp.id);
    setForm({
      first_name: emp.first_name,
      last_name: emp.last_name,
      initials: emp.initials,
      contract_hours: String(emp.contract_hours),
      company_id: emp.company_id || 'none',
    });
    setShowAdd(false);
    setAccountForm({ ...accountForm, open: false });
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      await employeesAPI.update(editId, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        initials: form.initials.trim(),
        contract_hours: parseFloat(form.contract_hours),
        company_id: form.company_id && form.company_id !== 'none' ? form.company_id : null,
      });
      toast.success('Employé mis à jour');
      resetForm(); fetchEmployees(); onSave();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet employé et tous ses shifts ?')) return;
    setLoading(true);
    try {
      await employeesAPI.delete(id);
      toast.success('Employé supprimé');
      fetchEmployees(); onSave();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setLoading(false);
  };

  const handleCreateAccount = async () => {
    if (!accountForm.email.trim() || !accountForm.password.trim()) {
      toast.error('Remplissez email et mot de passe'); return;
    }
    setLoading(true);
    try {
      await authAPI.createEmployeeAccount({
        email: accountForm.email.trim(),
        password: accountForm.password.trim(),
        employee_id: accountForm.employeeId,
      });
      toast.success('Compte employé créé');
      setAccountForm({ open: false, employeeId: null, email: '', password: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="employee-manage-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Gestion des employés</DialogTitle>
          <DialogDescription>{employees.length} employé(s)</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <ScrollArea className="max-h-[280px]">
            <div className="space-y-2">
              {employees.map(emp => (
                <div key={emp.id} data-testid={`employee-item-${emp.id}`}
                  className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold text-slate-400 font-data">{emp.initials}</span>
                      <span className="text-sm font-medium truncate">{emp.first_name} {emp.last_name.toUpperCase()}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-slate-400 font-data">{emp.contract_hours}h/sem</span>
                      {emp.company_id && (
                        <span className="text-[10px] font-medium bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full">
                          {getCompanyName(emp.company_id)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`account-employee-${emp.id}`}
                      onClick={() => setAccountForm({ open: true, employeeId: emp.id, email: '', password: '' })}
                      title="Créer un compte">
                      <KeyRound className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-employee-${emp.id}`}
                      onClick={() => handleEdit(emp)}>
                      <Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700"
                      data-testid={`delete-employee-${emp.id}`} onClick={() => handleDelete(emp.id)}>
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          <Separator />

          {/* Create employee account form */}
          {accountForm.open && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3 animate-fade-in-up">
              <Label className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
                <KeyRound className="w-3.5 h-3.5" strokeWidth={1.5} />
                Créer un compte de connexion
              </Label>
              <Input data-testid="account-email-input" type="email" placeholder="Email de connexion"
                value={accountForm.email} onChange={e => setAccountForm({ ...accountForm, email: e.target.value })} />
              <Input data-testid="account-password-input" type="password" placeholder="Mot de passe"
                value={accountForm.password} onChange={e => setAccountForm({ ...accountForm, password: e.target.value })} />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" data-testid="account-cancel-btn"
                  onClick={() => setAccountForm({ open: false, employeeId: null, email: '', password: '' })}>
                  <X className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />Annuler
                </Button>
                <Button size="sm" data-testid="account-save-btn" onClick={handleCreateAccount} disabled={loading}>
                  <KeyRound className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />Créer le compte
                </Button>
              </div>
            </div>
          )}

          {/* Add/Edit form */}
          {(showAdd || editId) ? (
            <div className="bg-slate-50 rounded-lg p-4 space-y-3 animate-fade-in-up">
              <Label className="text-sm font-semibold">
                {editId ? "Modifier l'employé" : 'Nouvel employé'}
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Prénom</Label>
                  <Input data-testid="employee-first-name" value={form.first_name}
                    onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder="Prénom" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Nom</Label>
                  <Input data-testid="employee-last-name" value={form.last_name}
                    onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="Nom" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Initiales</Label>
                  <Input data-testid="employee-initials" value={form.initials}
                    onChange={e => setForm({ ...form, initials: e.target.value.toUpperCase().slice(0, 3) })}
                    placeholder="AB" maxLength={3} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Contrat</Label>
                  <Select value={form.contract_hours} onValueChange={v => setForm({ ...form, contract_hours: v })}>
                    <SelectTrigger data-testid="employee-contract-hours"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="20">20h</SelectItem>
                      <SelectItem value="25">25h</SelectItem>
                      <SelectItem value="30">30h</SelectItem>
                      <SelectItem value="35">35h</SelectItem>
                      <SelectItem value="37">37h</SelectItem>
                      <SelectItem value="39">39h</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Société</Label>
                  <Select value={form.company_id} onValueChange={v => setForm({ ...form, company_id: v })}>
                    <SelectTrigger data-testid="employee-company-select"><SelectValue placeholder="Aucune" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      {companies.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button data-testid="employee-cancel-btn" variant="outline" size="sm" onClick={resetForm}>
                  <X className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />Annuler
                </Button>
                <Button data-testid="employee-save-btn" size="sm" onClick={editId ? handleUpdate : handleAdd} disabled={loading}>
                  <Save className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />{editId ? 'Mettre à jour' : 'Ajouter'}
                </Button>
              </div>
            </div>
          ) : !accountForm.open && (
            <Button data-testid="add-employee-btn" variant="outline" className="w-full" onClick={() => setShowAdd(true)}>
              <UserPlus className="w-4 h-4 mr-2" strokeWidth={1.5} />Ajouter un employé
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
